<template>
  <Teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-container">
        <!-- Close button -->
        <button class="close-btn" @click="$emit('close')" aria-label="Close modal">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>

        <!-- Header -->
        <div class="modal-header">
            <div class="command-badge">
              <span class="command-slash">/</span>
              <span class="command-name">{{ command.name }}</span>
            </div>
            <span class="category-tag">{{ command.category }}</span>
          </div>

          <!-- Description -->
          <div class="modal-body">
            <p class="description">{{ command.description }}</p>

            <!-- Usage Section -->
            <div class="info-section">
              <div class="section-header">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="4 17 10 11 4 5"></polyline>
                  <line x1="12" x2="20" y1="19" y2="19"></line>
                </svg>
                <h3>Usage</h3>
              </div>
              <div class="code-block">
                <code>/{{ command.usage }}</code>
                <button class="copy-btn" @click="copyToClipboard('/' + command.usage)" :class="{ 'copied': copiedUsage }">
                  <svg v-if="!copiedUsage" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
              </div>
              <div v-if="command.usage2" class="code-block alternate">
                <code>/{{ command.usage2 }}</code>
              </div>
            </div>

            <!-- Options Section -->
            <div class="info-section" v-if="command.options && command.options.length > 0">
              <div class="section-header">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M12 1v6m0 6v10"></path>
                  <path d="m5.6 5.6 4.3 4.3m4.2 4.2 4.3 4.3"></path>
                  <path d="M1 12h6m6 0h10"></path>
                  <path d="m5.6 18.4 4.3-4.3m4.2-4.2 4.3-4.3"></path>
                </svg>
                <h3>Options</h3>
              </div>
              <div class="options-list">
                <div v-for="option in command.options" :key="option.name" class="option-item">
                  <div class="option-header">
                    <span class="option-name">{{ option.name }}</span>
                    <span class="option-required" :class="option.required ? 'required' : 'optional'">
                      {{ option.required ? 'Required' : 'Optional' }}
                    </span>
                  </div>
                  <p class="option-description">{{ option.description }}</p>
                </div>
              </div>
            </div>

            <!-- Permissions Section -->
            <div class="info-section" v-if="command.permissions && command.permissions.length > 0">
              <div class="section-header">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"></path>
                </svg>
                <h3>Required Permissions</h3>
              </div>
              <div class="permissions-list">
                <span v-for="permission in command.permissions" :key="permission" class="permission-badge">
                  {{ formatPermission(permission) }}
                </span>
              </div>
            </div>

            <!-- Aliases Section -->
            <div class="info-section" v-if="command.aliases && command.aliases.length > 0">
              <div class="section-header">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
                <h3>Aliases</h3>
              </div>
              <div class="aliases-list">
                <span v-for="alias in command.aliases" :key="alias" class="alias-badge">
                  /{{ alias }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
  </Teleport>
</template>

<script>
export default {
  props: {
    command: Object
  },
  data() {
    return {
      copiedUsage: false
    };
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = 'hidden';
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    },
    formatPermission(permission) {
      return permission
        .split('_')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.copiedUsage = true;
        setTimeout(() => {
          this.copiedUsage = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  }
};
</script>

<style scoped>
/* Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-index-modal, 400);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md);
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  animation: overlayFadeIn 0.25s ease-out;
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Container */
.modal-container {
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  overflow-y: auto;
  background: linear-gradient(165deg, var(--color-bg-tertiary, #1e1f26) 0%, var(--color-bg-secondary, #16171c) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--border-radius-xl, 24px);
  box-shadow: 
    0 24px 48px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  animation: modalSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Custom scrollbar for modal */
.modal-container::-webkit-scrollbar {
  width: 6px;
}

.modal-container::-webkit-scrollbar-track {
  background: transparent;
}

.modal-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

/* Close button */
.close-btn {
  position: absolute;
  top: var(--spacing-md, 1rem);
  right: var(--spacing-md, 1rem);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: var(--color-text-secondary, #b8bcc8);
  cursor: pointer;
  transition: all var(--transition-normal, 0.3s ease);
  z-index: 10;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-text-primary, #fff);
  transform: rotate(90deg);
}

.close-btn svg {
  width: 18px;
  height: 18px;
}

/* Header */
.modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md, 1rem);
  padding: var(--spacing-xl, 2rem) var(--spacing-xl, 2rem) var(--spacing-lg, 1.5rem);
  text-align: center;
  background: linear-gradient(180deg, rgba(88, 101, 242, 0.1) 0%, transparent 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.command-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 0.1em;
  font-size: var(--font-size-xxl, 2.5rem);
  font-weight: 700;
  background: var(--color-gradient-primary, linear-gradient(135deg, #00b7ff, #fe5ea1));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.command-slash {
  opacity: 1;
}

.category-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 1rem;
  background: rgba(88, 101, 242, 0.2);
  border: 1px solid rgba(88, 101, 242, 0.3);
  border-radius: 20px;
  color: var(--color-accent-discord, #5865f2);
  font-size: var(--font-size-sm, 0.9rem);
  font-weight: 600;
}

/* Body */
.modal-body {
  padding: var(--spacing-lg, 1.5rem) var(--spacing-xl, 2rem) var(--spacing-xl, 2rem);
}

.description {
  font-size: var(--font-size-md, 1.1rem);
  line-height: 1.7;
  color: var(--color-text-secondary, #b8bcc8);
  text-align: center;
  margin-bottom: var(--spacing-lg, 1.5rem);
}

/* Info sections */
.info-section {
  margin-top: var(--spacing-lg, 1.5rem);
  padding-top: var(--spacing-lg, 1.5rem);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.info-section:first-of-type {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: var(--spacing-md, 1rem);
}

.section-header svg {
  width: 18px;
  height: 18px;
  color: var(--color-accent-discord, #5865f2);
}

.section-header h3 {
  font-size: var(--font-size-sm, 1rem);
  font-weight: 600;
  color: var(--color-text-primary, #fff);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

/* Code block */
.code-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm, 0.75rem);
  background: var(--color-bg-primary, #0f1015);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--border-radius-md, 12px);
  padding: var(--spacing-md, 1rem);
  margin-bottom: var(--spacing-sm, 0.5rem);
}

.code-block.alternate {
  background: rgba(88, 101, 242, 0.08);
  border-color: rgba(88, 101, 242, 0.2);
}

.code-block code {
  font-family: var(--font-mono, 'JetBrains Mono', Consolas, monospace);
  font-size: 0.95rem;
  color: var(--color-text-primary, #fff);
  background: none;
  padding: 0;
  border: none;
  word-break: break-all;
}

.copy-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-sm, 8px);
  color: var(--color-text-muted, #8b8fa3);
  cursor: pointer;
  transition: all var(--transition-normal, 0.3s ease);
}

.copy-btn:hover {
  background: rgba(88, 101, 242, 0.2);
  border-color: var(--color-accent-discord, #5865f2);
  color: var(--color-accent-discord, #5865f2);
}

.copy-btn.copied {
  background: rgba(34, 197, 94, 0.2);
  border-color: #22c55e;
  color: #22c55e;
}

.copy-btn svg {
  width: 16px;
  height: 16px;
}

/* Options list */
.options-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm, 0.75rem);
}

.option-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--border-radius-md, 12px);
  padding: var(--spacing-md, 1rem);
  transition: all var(--transition-normal, 0.3s ease);
}

.option-item:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}

.option-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm, 0.5rem);
  margin-bottom: 0.4rem;
}

.option-name {
  font-family: var(--font-mono, 'JetBrains Mono', Consolas, monospace);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-text-primary, #fff);
}

.option-required {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.option-required.required {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.option-required.optional {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}

.option-description {
  font-size: var(--font-size-sm, 0.9rem);
  color: var(--color-text-secondary, #b8bcc8);
  line-height: 1.5;
  margin: 0;
}

/* Permissions list */
.permissions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.permission-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.8rem;
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.25);
  border-radius: 8px;
  color: #fbbf24;
  font-size: 0.8rem;
  font-weight: 600;
}

/* Aliases list */
.aliases-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.alias-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.8rem;
  background: rgba(139, 92, 246, 0.12);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: 8px;
  color: #a78bfa;
  font-family: var(--font-mono, 'JetBrains Mono', Consolas, monospace);
  font-size: 0.85rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    max-height: 90vh;
  }

  .modal-header {
    padding: var(--spacing-lg, 1.5rem) var(--spacing-lg, 1.5rem) var(--spacing-md, 1rem);
  }

  .command-badge {
    font-size: var(--font-size-xl, 2rem);
  }

  .modal-body {
    padding: var(--spacing-md, 1rem) var(--spacing-lg, 1.5rem) var(--spacing-lg, 1.5rem);
  }

  .close-btn {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: var(--spacing-sm, 0.5rem);
  }

  .modal-header {
    padding: var(--spacing-md, 1rem);
    padding-top: var(--spacing-xl, 2.5rem);
  }

  .command-badge {
    font-size: var(--font-size-lg, 1.75rem);
  }

  .modal-body {
    padding: var(--spacing-md, 1rem);
  }

  .option-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
  }

  .code-block {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-sm, 0.5rem);
  }

  .copy-btn {
    width: 100%;
    height: 36px;
  }
}

/* Focus states */
.close-btn:focus-visible,
.copy-btn:focus-visible {
  outline: 2px solid var(--color-accent-discord, #5865f2);
  outline-offset: 2px;
}
</style>